import {useId, useState} from 'react'
import {sharpetErrorMessage, useAdoptSharpet} from 'hooks/useSharpet'
import {cn} from 'utils/cn'
import {DEFAULT_SPECIES} from './manifest'
import SharpetSpeciesPicker from './SharpetSpeciesPicker'

export const SHARPET_NAME_MAX_LENGTH = 24

/**
 * Pick a pet, name it and it is yours. Used by the corner dock and the
 * Sharpennies page card; adopting through either updates both, because
 * they share the useSharpet cache.
 *
 * Pass `species` and `onSpeciesChange` to show the pick elsewhere (a big
 * preview above the form); otherwise the form keeps it to itself.
 */
const AdoptSharpetForm = ({
  species: controlledSpecies,
  onSpeciesChange,
  onAdopted,
  className,
}) => {
  const [ownSpecies, setOwnSpecies] = useState(DEFAULT_SPECIES)
  const species = controlledSpecies ?? ownSpecies
  const setSpecies = onSpeciesChange ?? setOwnSpecies
  const [name, setName] = useState('')
  const adopt = useAdoptSharpet()
  const inputId = useId()

  const submit = (event) => {
    event.preventDefault()
    if (!name.trim() || adopt.isLoading) return
    adopt.mutate({name, species}, {onSuccess: (data) => onAdopted?.(data)})
  }

  return (
    <form onSubmit={submit} className={cn('flex flex-col gap-2', className)}>
      <SharpetSpeciesPicker value={species} onChange={setSpecies} />
      <label
        className="mt-1 text-sm font-medium text-gray-700"
        htmlFor={inputId}
      >
        What will you call it?
      </label>
      <div className="flex gap-2">
        <input
          id={inputId}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={SHARPET_NAME_MAX_LENGTH}
          placeholder="Biscuit"
          autoComplete="off"
          className="min-w-0 flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sharpenn-coral/40"
        />
        <button
          type="submit"
          disabled={!name.trim() || adopt.isLoading}
          className="shrink-0 rounded-full bg-sharpenn-black px-4 py-2 text-sm font-semibold text-white hover:bg-sharpenn-charcoal disabled:cursor-not-allowed disabled:opacity-50"
        >
          {adopt.isLoading ? 'Adopting…' : 'Adopt'}
        </button>
      </div>
      {adopt.isError && (
        <p className="text-sm text-red-600">
          {sharpetErrorMessage(adopt.error, 'That did not work. Try again.')}
        </p>
      )}
    </form>
  )
}

export default AdoptSharpetForm
